import { useState, useMemo } from 'react';

interface PerpTokenData {
  token: string;
  source: string;
}

interface usePerpetualOptionsProps {
  perpsList: PerpTokenData[];
}

const getMainTokensForSource = (source: string, allTokenOptions: any[]) => {
  if (source === 'DERIBIT') {
    return ['BTC_USDC', 'ETH_USDC', 'SOL_USDC', 'XRP_USDC'];
  }
  if (source === 'BINANCE') {
    return ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT'];
  }
  return allTokenOptions.slice(0, 5).map((t) => t.value);
};

const formatTokenLabel = (token: string) => token.replace(/(_)?(USDC|USDT|USD)$/g, '');

export const usePerpetualOptions = (props: usePerpetualOptionsProps) => {
  const { perpsList } = props;

  const [source, setSource] = useState<string>('DERIBIT');
  const [token, setToken] = useState<string>('');

  const handleSourceChange = (newSource: string) => {
    setSource(newSource);
    const mainTokens = getMainTokensForSource(newSource, []);
    if (mainTokens.length > 0) {
      const sourceTokens = perpsList.filter((p) => p.source === newSource);
      const availableMainToken = sourceTokens.find((t) => mainTokens.includes(t.token));
      if (availableMainToken) {
        setToken(availableMainToken.token);
      }
    }
  };

  const handleSources = (perpsList: PerpTokenData[]) => {
    const uniqueSources = [...new Set(perpsList.map((p) => p.source))];
    const sourceOptions = uniqueSources.map((s) => ({
      label: s,
      value: s,
    }));

    return sourceOptions;
  };

  const handleTokens = (perpsList: PerpTokenData[], source: string) => {
    const sourceTokens = perpsList.filter((p) => p.source === source);

    const allTokenOptions = sourceTokens.map((t) => ({
      label: formatTokenLabel(t.token),
      value: t.token,
    }));

    const mainTokenValues = getMainTokensForSource(source, allTokenOptions);
    const mainOptions = allTokenOptions.filter((t) => mainTokenValues.includes(t.value));
    const otherOptions = allTokenOptions.filter((t) => !mainTokenValues.includes(t.value));

    if (allTokenOptions.length > 0 && !allTokenOptions.find((t) => t.value === token)) {
      setToken(allTokenOptions[0].value);
    }

    return { mainOptions, otherOptions };
  };

  const options = useMemo(() => {
    if (!perpsList || perpsList.length === 0) {
      return [];
    }

    const sourceOptions = handleSources(perpsList);
    const { mainOptions: tokenOptions, otherOptions: otherTokenOptions } = handleTokens(
      perpsList,
      source,
    );

    return [
      {
        key: 'source',
        value: source,
        setValue: handleSourceChange,
        label: 'Source',
        options: sourceOptions,
      },
      {
        key: 'token',
        value: token,
        setValue: setToken,
        label: 'Token',
        options: tokenOptions,
        otherOptions: otherTokenOptions,
      },
    ];
  }, [source, token, perpsList]);

  return {
    options,
  };
};
